import React from 'react';
import UnifiedSimpleConversionExplanation from './UnifiedSimpleConversionExplanation';

const ElectricChargeConversionExplanation = ({
  fromUnit,
  toUnit,
  inputValue,
  result,
  darkMode
}) => {
  const getConversionData = () => {
    // Coulombs to other units
    if (fromUnit === 'Coulombs' && toUnit === 'Millicoulombs') {
      return {
        formula: 'mC = C × 1000',
        conversionSteps: [
          `Take the charge in coulombs: ${inputValue}C`,
          `Multiply by 1000: ${inputValue} × 1000 = ${result}mC`
        ],
        realWorldExample: "A typical smartphone battery holds a charge of about 10,000 coulombs.",
        funFacts: [
          "The coulomb is named after Charles-Augustin de Coulomb.",
          "One coulomb represents about 6.242 × 10¹⁸ elementary charges.",
          "A typical lightning bolt transfers 15-20 coulombs of charge.",
        ]
      };
    }

    if (fromUnit === 'Coulombs' && toUnit === 'Ampere-hours') {
      return {
        formula: 'Ah = C ÷ 3600',
        conversionSteps: [
          `Take the charge in coulombs: ${inputValue}C`,
          `Divide by 3600 (seconds in an hour): ${inputValue} ÷ 3600 = ${result}Ah`
        ],
        realWorldExample: "A typical car battery has a capacity of about 50 ampere-hours.",
        funFacts: [
          "Ampere-hours are commonly used to rate battery capacity.",
          "One ampere-hour equals 3600 coulombs.",
          "Modern electric car batteries can exceed 100 ampere-hours.",
        ]
      };
    }

    if (fromUnit === 'Microcoulombs' && toUnit === 'Nanocoulombs') {
      return {
        formula: 'nC = µC × 1000',
        conversionSteps: [
          `Take the charge in microcoulombs: ${inputValue}µC`,
          `Multiply by 1000: ${inputValue} × 1000 = ${result}nC`
        ],
        realWorldExample: "Static electricity buildup can involve charges of a few microcoulombs.",
        funFacts: [
          "Static electricity can be measured in micro or nanocoulombs.",
          "The charge of one electron is about -1.602 × 10⁻¹⁹ coulombs.",
          "Capacitors often store charge in the micro to nanocoulomb range.",
        ]
      };
    }

    if (fromUnit === 'Picocoulombs' && toUnit === 'Nanocoulombs') {
      return {
        formula: 'nC = pC ÷ 1000',
        conversionSteps: [
          `Take the charge in picocoulombs: ${inputValue}pC`,
          `Divide by 1000: ${inputValue} ÷ 1000 = ${result}nC`
        ],
        realWorldExample: "Some sensitive electronic measurements deal with picocoulomb-level charges.",
        funFacts: [
          "Picocoulombs are used in measuring very small electrical charges.",
          "Modern electronic sensors can detect charges as small as a few picocoulombs.",
          "Quantum electronic devices often operate at the picocoulomb scale.",
        ]
      };
    }

    if (fromUnit === 'Ampere-hours' && toUnit === 'Millicoulombs') {
      return {
        formula: 'mC = Ah × 3,600,000',
        conversionSteps: [
          `Take the charge in ampere-hours: ${inputValue}Ah`,
          `Multiply by 3,600,000: ${inputValue} × 3,600,000 = ${result}mC`
        ],
        realWorldExample: "A smartphone battery rated at 3000mAh holds 10,800,000 millicoulombs of charge.",
        funFacts: [
          "Battery capacity is usually specified in milliampere-hours (mAh).",
          "Larger batteries like those in EVs are rated in ampere-hours (Ah).",
          "The conversion between Ah and coulombs involves the number of seconds in an hour.",
        ]
      };
    }

    // Default case for other conversions
    return {
      formula: `Converting from ${fromUnit} to ${toUnit}`,
      conversionSteps: [
        `Start with ${inputValue} ${fromUnit}`,
        `Apply conversion factor`,
        `Result: ${result} ${toUnit}`
      ],
      realWorldExample: "Electric charge measurements are essential in electronics and battery technology.",
      funFacts: [
        "Electric charge is a fundamental property of matter.",
        "The SI unit of electric charge is the coulomb.",
        "Electric charge is conserved in all known physical processes.",
      ]
    };
  };

  const conversionData = getConversionData();

  return (
    <UnifiedSimpleConversionExplanation
      conversionType="electricCharge"
      fromUnit={fromUnit}
      toUnit={toUnit}
      inputValue={inputValue}
      result={result}
      darkMode={darkMode}
      {...conversionData}
    />
  );
};

export default ElectricChargeConversionExplanation; 